import React, { useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";
import Icon from "components/AppIcon";

const formatRevenue = (v) => {
  if (v >= 10000000) return `₹${(v / 10000000)?.toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000)?.toFixed(1)}L`;
  return `₹${v?.toLocaleString('en-IN')}`;
};
const formatCustomers = (v) => v?.toLocaleString();

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-md border p-3 text-sm"
      style={{
        background: "var(--color-popover)",
        borderColor: "var(--color-border)",
        boxShadow: "var(--shadow-lg)",
        fontFamily: "var(--font-caption)",
      }}
    >
      <p className="font-semibold mb-2" style={{ color: "var(--color-foreground)" }}>{label}</p>
      {payload?.map((entry) => (
        <div key={entry?.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: entry?.color }} />
          <span style={{ color: "var(--color-text-secondary)" }}>{entry?.name}:</span>
          <span className="font-data font-semibold" style={{ color: "var(--color-foreground)" }}>
            {entry?.name === "Revenue" ? formatRevenue(entry?.value) : formatCustomers(entry?.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function RevenueCustomerChart({ data }) {
  const [activeBar, setActiveBar] = useState(null);
  const monthlyTrend = data || [];

  return (
    <div
      className="card-base flex flex-col gap-4 min-w-0"
      style={{ background: "var(--color-card)", borderRadius: "var(--radius-md)" }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg" style={{ color: "var(--color-foreground)" }}>
            Revenue &amp; Customer Growth
          </h3>
          <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            Monthly revenue bars with customer count overlay — last {monthlyTrend?.length || 6} months
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: "var(--color-muted)" }}>
          <Icon name="Calendar" size={12} color="var(--color-text-secondary)" />
          <span className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            {monthlyTrend?.length || 6}-month view
          </span>
        </div>
      </div>

      <div className="w-full" style={{ height: "300px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={monthlyTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fontFamily: "var(--font-caption)", fill: "var(--color-text-secondary)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="revenue"
              orientation="left"
              tickFormatter={formatRevenue}
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "var(--color-text-secondary)" }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <YAxis
              yAxisId="customers"
              orientation="right"
              tickFormatter={formatCustomers}
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "var(--color-text-secondary)" }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontFamily: "var(--font-caption)", fontSize: "12px", paddingTop: "8px" }}
            />
            <Bar
              yAxisId="revenue"
              dataKey="revenue"
              name="Revenue"
              fill="var(--color-primary)"
              radius={[4, 4, 0, 0]}
              opacity={0.85}
              onMouseEnter={(_, idx) => setActiveBar(idx)}
              onMouseLeave={() => setActiveBar(null)}
            />
            <Line
              yAxisId="customers"
              dataKey="customers"
              name="Customers"
              stroke="var(--color-accent)"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "var(--color-accent)", strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              type="monotone"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}